import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock3,
  Download,
  FileCheck2,
  Filter,
  PackageCheck,
  Paperclip,
  Plus,
  ReceiptText,
  Search,
  ShoppingCart,
  WalletCards,
  X,
} from "lucide-react";

import TitleButtons from "../../../../components/TitleButtons";
import { useTheme } from "../../../../context/ThemeContext";

type SectionKey =
  | "visao-geral"
  | "pendencias"
  | "aguardando-pagamento"
  | "pagas"
  | "canceladas";

type StatusCompra =
  | "rascunho"
  | "em_cotacao"
  | "aguardando_aprovacao"
  | "aprovada"
  | "aguardando_pagamento"
  | "paga"
  | "recebida"
  | "cancelada";

type TipoDocumentoCompra =
  | "comprovante_pagamento"
  | "nota_fiscal"
  | "detalhamento_compra";

type StatusDocumentoCompra = "anexado" | "pendente" | "nao_aplicavel";

type DocumentoCompra = {
  tipo: TipoDocumentoCompra;
  nome: string;
  status: StatusDocumentoCompra;
  arquivoNome: string | null;
  anexadoEm: string | null;
  responsavel: string | null;
};

type Compra = {
  id: number;
  codigo: string;
  fornecedor: string;
  cnpjCpf: string;
  categoria: string;
  centroCusto: string;
  solicitante: string;
  responsavel: string;
  dataSolicitacao: string;
  dataVencimento: string | null;
  dataPagamento: string | null;
  valorTotal: number;
  formaPagamento: string;
  status: StatusCompra;
  prioridade: "baixa" | "normal" | "alta" | "critica";
  descricao: string;
  observacao: string | null;
  documentos: DocumentoCompra[];
};

type FilterState = {
  busca: string;
  status: string;
  categoria: string;
  centroCusto: string;
  pendencia: string;
};

type ComprasLayoutProps = {
  section?: SectionKey;
};

const DEPARTMENT_COLOR = "#2f3f59";

const REQUIRED_DOCUMENTS: Array<{
  tipo: TipoDocumentoCompra;
  label: string;
}> = [
  {
    tipo: "comprovante_pagamento",
    label: "Comprovante de pagamento",
  },
  {
    tipo: "nota_fiscal",
    label: "Nota fiscal",
  },
  {
    tipo: "detalhamento_compra",
    label: "Detalhamento da compra",
  },
];

const fixedCompras: Compra[] = [
  {
    id: 1,
    codigo: "CP-2026-0001",
    fornecedor: "Amazon Web Services",
    cnpjCpf: "Não informado",
    categoria: "Infraestrutura em nuvem",
    centroCusto: "Tecnologia / Logvi",
    solicitante: "Guilherme Brito",
    responsavel: "Guilherme Brito",
    dataSolicitacao: "2026-05-02",
    dataVencimento: "2026-05-12",
    dataPagamento: "2026-05-08",
    valorTotal: 1847.9,
    formaPagamento: "Cartão corporativo",
    status: "paga",
    prioridade: "alta",
    descricao: "Créditos e serviços AWS para ambiente Logvi/HAVOX.",
    observacao:
      "Compra relacionada à sustentação de infraestrutura, banco de dados, API e hospedagem.",
    documentos: [
      {
        tipo: "comprovante_pagamento",
        nome: "Comprovante de pagamento",
        status: "anexado",
        arquivoNome: "CP-2026-0001-comprovante.pdf",
        anexadoEm: "2026-05-08",
        responsavel: "Guilherme Brito",
      },
      {
        tipo: "nota_fiscal",
        nome: "Nota fiscal",
        status: "anexado",
        arquivoNome: "CP-2026-0001-nf.pdf",
        anexadoEm: "2026-05-09",
        responsavel: "Financeiro",
      },
      {
        tipo: "detalhamento_compra",
        nome: "Detalhamento da compra",
        status: "anexado",
        arquivoNome: "CP-2026-0001-detalhamento.pdf",
        anexadoEm: "2026-05-09",
        responsavel: "Tecnologia",
      },
    ],
  },
  {
    id: 2,
    codigo: "CP-2026-0002",
    fornecedor: "Kalunga",
    cnpjCpf: "43.283.811/0001-50",
    categoria: "Material de escritório",
    centroCusto: "Administrativo",
    solicitante: "Rayane Santos",
    responsavel: "Suprimentos",
    dataSolicitacao: "2026-05-03",
    dataVencimento: "2026-05-15",
    dataPagamento: null,
    valorTotal: 392.4,
    formaPagamento: "Boleto",
    status: "aguardando_pagamento",
    prioridade: "normal",
    descricao:
      "Materiais de escritório, pastas, etiquetas e insumos administrativos.",
    observacao: "Aguardando pagamento para liberar baixa documental.",
    documentos: [
      {
        tipo: "comprovante_pagamento",
        nome: "Comprovante de pagamento",
        status: "pendente",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "nota_fiscal",
        nome: "Nota fiscal",
        status: "anexado",
        arquivoNome: "CP-2026-0002-nf.pdf",
        anexadoEm: "2026-05-04",
        responsavel: "Suprimentos",
      },
      {
        tipo: "detalhamento_compra",
        nome: "Detalhamento da compra",
        status: "anexado",
        arquivoNome: "CP-2026-0002-detalhamento.pdf",
        anexadoEm: "2026-05-04",
        responsavel: "Suprimentos",
      },
    ],
  },
  {
    id: 3,
    codigo: "CP-2026-0003",
    fornecedor: "Fornecedor Técnico RJ",
    cnpjCpf: "12.345.678/0001-90",
    categoria: "Componentes eletrônicos",
    centroCusto: "Laboratório / Reparos",
    solicitante: "Técnico Laboratório",
    responsavel: "Suprimentos",
    dataSolicitacao: "2026-05-05",
    dataVencimento: null,
    dataPagamento: null,
    valorTotal: 1280,
    formaPagamento: "Pix",
    status: "aguardando_aprovacao",
    prioridade: "critica",
    descricao:
      "Componentes para bancada de testes e reposição em reparos marítimos.",
    observacao: "Validar se a compra deve ser vinculada a uma O.S. específica.",
    documentos: [
      {
        tipo: "comprovante_pagamento",
        nome: "Comprovante de pagamento",
        status: "pendente",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "nota_fiscal",
        nome: "Nota fiscal",
        status: "pendente",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "detalhamento_compra",
        nome: "Detalhamento da compra",
        status: "anexado",
        arquivoNome: "CP-2026-0003-detalhamento.pdf",
        anexadoEm: "2026-05-05",
        responsavel: "Técnico Laboratório",
      },
    ],
  },
  {
    id: 4,
    codigo: "CP-2026-0004",
    fornecedor: "ABES",
    cnpjCpf: "Não informado",
    categoria: "Associação / Credenciamento",
    centroCusto: "Governança",
    solicitante: "Guilherme Brito",
    responsavel: "Diretoria",
    dataSolicitacao: "2026-05-06",
    dataVencimento: "2026-05-06",
    dataPagamento: "2026-05-06",
    valorTotal: 850,
    formaPagamento: "Pix",
    status: "recebida",
    prioridade: "normal",
    descricao: "Taxa associativa e documentação institucional.",
    observacao: "Manter comprovantes vinculados ao cadastro institucional.",
    documentos: [
      {
        tipo: "comprovante_pagamento",
        nome: "Comprovante de pagamento",
        status: "anexado",
        arquivoNome: "CP-2026-0004-comprovante.pdf",
        anexadoEm: "2026-05-06",
        responsavel: "Diretoria",
      },
      {
        tipo: "nota_fiscal",
        nome: "Nota fiscal",
        status: "pendente",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "detalhamento_compra",
        nome: "Detalhamento da compra",
        status: "anexado",
        arquivoNome: "CP-2026-0004-detalhamento.pdf",
        anexadoEm: "2026-05-06",
        responsavel: "Diretoria",
      },
    ],
  },
  {
    id: 5,
    codigo: "CP-2026-0005",
    fornecedor: "Loja de Informática Niterói",
    cnpjCpf: "98.765.432/0001-10",
    categoria: "Equipamentos de informática",
    centroCusto: "Infraestrutura interna",
    solicitante: "Guilherme Brito",
    responsavel: "Administrativo",
    dataSolicitacao: "2026-05-07",
    dataVencimento: null,
    dataPagamento: null,
    valorTotal: 0,
    formaPagamento: "Não definido",
    status: "em_cotacao",
    prioridade: "baixa",
    descricao: "Cotação de monitor, cabos, suporte e periféricos.",
    observacao: "Compra ainda sem valor fechado.",
    documentos: [
      {
        tipo: "comprovante_pagamento",
        nome: "Comprovante de pagamento",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "nota_fiscal",
        nome: "Nota fiscal",
        status: "nao_aplicavel",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
      {
        tipo: "detalhamento_compra",
        nome: "Detalhamento da compra",
        status: "pendente",
        arquivoNome: null,
        anexadoEm: null,
        responsavel: null,
      },
    ],
  },
];

const initialFilters: FilterState = {
  busca: "",
  status: "",
  categoria: "",
  centroCusto: "",
  pendencia: "",
};

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
}

function formatDate(value: string | null) {
  if (!value) return "Não informado";

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

function plural(value: number, singular: string, pluralText: string) {
  return value === 1 ? singular : pluralText;
}

function getStatusCompraLabel(status: StatusCompra) {
  switch (status) {
    case "rascunho":
      return "Rascunho";
    case "em_cotacao":
      return "Em cotação";
    case "aguardando_aprovacao":
      return "Aguardando aprovação";
    case "aprovada":
      return "Aprovada";
    case "aguardando_pagamento":
      return "Aguardando pagamento";
    case "paga":
      return "Paga";
    case "recebida":
      return "Recebida";
    case "cancelada":
      return "Cancelada";
    default:
      return "Indefinido";
  }
}

function getStatusCompraVisual(status: StatusCompra) {
  switch (status) {
    case "paga":
    case "recebida":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "aguardando_pagamento":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <WalletCards size={12} />,
      };

    case "aguardando_aprovacao":
    case "aprovada":
      return {
        background: "#f5f3ff",
        color: "#6d28d9",
        border: "#ddd6fe",
        icon: <Clock3 size={12} />,
      };

    case "em_cotacao":
    case "rascunho":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };

    case "cancelada":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <X size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getPrioridadeLabel(prioridade: Compra["prioridade"]) {
  switch (prioridade) {
    case "baixa":
      return "Baixa";
    case "normal":
      return "Normal";
    case "alta":
      return "Alta";
    case "critica":
      return "Crítica";
    default:
      return "Normal";
  }
}

function getDocumentoStatusLabel(status: StatusDocumentoCompra) {
  switch (status) {
    case "anexado":
      return "Anexado";
    case "pendente":
      return "Pendente";
    case "nao_aplicavel":
      return "Não aplicável";
    default:
      return "Indefinido";
  }
}

function getDocumentoStatusVisual(status: StatusDocumentoCompra) {
  switch (status) {
    case "anexado":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };

    case "pendente":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
        icon: <AlertTriangle size={12} />,
      };

    case "nao_aplicavel":
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };

    default:
      return {
        background: "#f8fafc",
        color: "#64748b",
        border: "#cbd5e1",
        icon: <Clock3 size={12} />,
      };
  }
}

function getDocumento(compra: Compra, tipo: TipoDocumentoCompra) {
  return compra.documentos.find((doc) => doc.tipo === tipo) || null;
}

function getPendenciasDocumentais(compra: Compra) {
  return REQUIRED_DOCUMENTS.filter((required) => {
    const documento = getDocumento(compra, required.tipo);

    if (!documento) return true;

    return documento.status === "pendente";
  });
}

function getResumoDocumental(compra: Compra) {
  const anexados = compra.documentos.filter(
    (doc) => doc.status === "anexado"
  ).length;

  const pendentes = compra.documentos.filter(
    (doc) => doc.status === "pendente"
  ).length;

  const naoAplicaveis = compra.documentos.filter(
    (doc) => doc.status === "nao_aplicavel"
  ).length;

  return {
    anexados,
    pendentes,
    naoAplicaveis,
    total: compra.documentos.length,
  };
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "pendencias":
      return "Pendências documentais";
    case "aguardando-pagamento":
      return "Aguardando pagamento";
    case "pagas":
      return "Compras pagas";
    case "canceladas":
      return "Compras canceladas";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const CompraRow: React.FC<{
  compra: Compra;
  departmentColor: string;
  onOpen: (compra: Compra) => void;
}> = ({ compra, departmentColor, onOpen }) => {
  const [hovered, setHovered] = useState(false);

  const statusVisual = getStatusCompraVisual(compra.status);
  const resumo = getResumoDocumental(compra);
  const pendencias = getPendenciasDocumentais(compra);
  const completa = pendencias.length === 0;

  return (
    <tr
      style={{
        ...styles.tr,
        background: hovered ? toRgba(departmentColor, 0.055) : "#ffffff",
        boxShadow: hovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      title="Duplo clique para abrir o detalhamento"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onOpen(compra)}
    >
      <td style={styles.td}>
        <div style={styles.codeCell}>
          <div
            style={{
              ...styles.codeIcon,
              color: hovered ? departmentColor : "#475569",
              borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef",
              background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc",
            }}
          >
            <ShoppingCart size={15} />
          </div>

          <div style={styles.codeInfo}>
            <div
              style={{
                ...styles.codeText,
                color: hovered ? departmentColor : "#111827",
              }}
            >
              {compra.codigo}
            </div>
            <div style={styles.mutedText}>
              {formatDate(compra.dataSolicitacao)}
            </div>
          </div>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{compra.fornecedor}</div>
        <div style={styles.mutedText}>{compra.cnpjCpf}</div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{compra.categoria}</div>
        <div style={styles.mutedText}>{compra.centroCusto}</div>
      </td>

      <td style={styles.td}>
        <Badge
          background={statusVisual.background}
          color={statusVisual.color}
          border={statusVisual.border}
          icon={statusVisual.icon}
        >
          {getStatusCompraLabel(compra.status)}
        </Badge>
      </td>

      <td style={styles.td}>
        <div style={styles.valueText}>{formatCurrency(compra.valorTotal)}</div>
        <div style={styles.mutedText}>{compra.formaPagamento}</div>
      </td>

      <td style={styles.td}>
        <div style={styles.documentSummary}>
          <span style={styles.documentOk}>{resumo.anexados} anexado(s)</span>
          {resumo.pendentes > 0 ? (
            <span style={styles.documentPending}>
              {resumo.pendentes} pendente(s)
            </span>
          ) : (
            <span style={styles.documentComplete}>sem pendência</span>
          )}
        </div>
      </td>

      <td style={styles.tdNoRightBorder}>
        {completa ? (
          <Badge
            background="#ecfdf3"
            color="#166534"
            border="#bbf7d0"
            icon={<CheckCircle2 size={12} />}
          >
            Completa
          </Badge>
        ) : (
          <Badge
            background="#fff7ed"
            color="#9a3412"
            border="#fed7aa"
            icon={<AlertTriangle size={12} />}
          >
            {pendencias.length} pendência(s)
          </Badge>
        )}
      </td>
    </tr>
  );
};

const CompraDetailOverlay: React.FC<{
  compra: Compra | null;
  departmentColor: string;
  onClose: () => void;
}> = ({ compra, departmentColor, onClose }) => {
  if (!compra) return null;

  const pendencias = getPendenciasDocumentais(compra);
  const completa = pendencias.length === 0;
  const statusVisual = getStatusCompraVisual(compra.status);

  return (
    <div style={styles.overlayBackdrop}>
      <div style={styles.overlayPanel}>
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>Compras / Detalhamento</div>
            <h2 style={styles.overlayTitle}>{compra.codigo}</h2>
            <p style={styles.overlaySubtitle}>
              Conferência documental da compra, anexos obrigatórios, pagamento,
              nota fiscal e detalhamento.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
          >
            <X size={17} />
          </button>
        </div>

        <div style={styles.detailContent}>
          <section style={styles.detailTopGrid}>
            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Fornecedor</span>
              <strong style={styles.detailValue}>{compra.fornecedor}</strong>
              <span style={styles.detailMeta}>{compra.cnpjCpf}</span>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Valor total</span>
              <strong style={styles.detailValue}>
                {formatCurrency(compra.valorTotal)}
              </strong>
              <span style={styles.detailMeta}>{compra.formaPagamento}</span>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Status da compra</span>
              <div style={styles.detailBadgeArea}>
                <Badge
                  background={statusVisual.background}
                  color={statusVisual.color}
                  border={statusVisual.border}
                  icon={statusVisual.icon}
                >
                  {getStatusCompraLabel(compra.status)}
                </Badge>
              </div>
            </div>

            <div style={styles.detailCard}>
              <span style={styles.detailLabel}>Conferência documental</span>
              <div style={styles.detailBadgeArea}>
                {completa ? (
                  <Badge
                    background="#ecfdf3"
                    color="#166534"
                    border="#bbf7d0"
                    icon={<CheckCircle2 size={12} />}
                  >
                    Completa
                  </Badge>
                ) : (
                  <Badge
                    background="#fff7ed"
                    color="#9a3412"
                    border="#fed7aa"
                    icon={<AlertTriangle size={12} />}
                  >
                    {pendencias.length} pendência(s)
                  </Badge>
                )}
              </div>
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <FileCheck2 size={15} />
              <strong>Documentos obrigatórios</strong>
            </div>

            <div style={styles.documentsGrid}>
              {REQUIRED_DOCUMENTS.map((required) => {
                const documento = getDocumento(compra, required.tipo);
                const status = documento?.status || "pendente";
                const visual = getDocumentoStatusVisual(status);

                return (
                  <div key={required.tipo} style={styles.documentCard}>
                    <div style={styles.documentCardHeader}>
                      <div style={styles.documentCardTitleArea}>
                        <Paperclip size={15} />
                        <strong style={styles.documentCardTitle}>
                          {required.label}
                        </strong>
                      </div>

                      <Badge
                        background={visual.background}
                        color={visual.color}
                        border={visual.border}
                        icon={visual.icon}
                      >
                        {getDocumentoStatusLabel(status)}
                      </Badge>
                    </div>

                    <div style={styles.documentInfoBlock}>
                      <span style={styles.documentInfoLabel}>Arquivo</span>
                      <span style={styles.documentInfoValue}>
                        {documento?.arquivoNome || "Nenhum arquivo anexado"}
                      </span>
                    </div>

                    <div style={styles.documentInfoBlock}>
                      <span style={styles.documentInfoLabel}>Anexado em</span>
                      <span style={styles.documentInfoValue}>
                        {formatDate(documento?.anexadoEm || null)}
                      </span>
                    </div>

                    <div style={styles.documentInfoBlock}>
                      <span style={styles.documentInfoLabel}>Responsável</span>
                      <span style={styles.documentInfoValue}>
                        {documento?.responsavel || "Não informado"}
                      </span>
                    </div>

                    <div style={styles.documentActions}>
                      <button
                        type="button"
                        style={{
                          ...styles.documentActionButton,
                          color:
                            status === "anexado" ? departmentColor : "#94a3b8",
                          borderColor:
                            status === "anexado"
                              ? toRgba(departmentColor, 0.35)
                              : "#e2e8f0",
                          cursor:
                            status === "anexado" ? "pointer" : "not-allowed",
                        }}
                        disabled={status !== "anexado"}
                      >
                        <Download size={13} />
                        Baixar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={styles.detailSection}>
            <div style={styles.detailSectionHeader}>
              <ReceiptText size={15} />
              <strong>Informações da compra</strong>
            </div>

            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <span>Descrição</span>
                <strong>{compra.descricao}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Categoria</span>
                <strong>{compra.categoria}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Centro de custo</span>
                <strong>{compra.centroCusto}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Prioridade</span>
                <strong>{getPrioridadeLabel(compra.prioridade)}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Solicitante</span>
                <strong>{compra.solicitante}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Responsável</span>
                <strong>{compra.responsavel}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Solicitação</span>
                <strong>{formatDate(compra.dataSolicitacao)}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Vencimento</span>
                <strong>{formatDate(compra.dataVencimento)}</strong>
              </div>

              <div style={styles.infoItem}>
                <span>Pagamento</span>
                <strong>{formatDate(compra.dataPagamento)}</strong>
              </div>
            </div>

            {compra.observacao && (
              <div style={styles.observationBox}>
                <strong>Observação</strong>
                <span>{compra.observacao}</span>
              </div>
            )}
          </section>
        </div>

        <div style={styles.overlayFooter}>
          <button type="button" style={styles.cancelButton} onClick={onClose}>
            Fechar
          </button>

          <button
            type="button"
            style={{
              ...styles.primaryButton,
              background: departmentColor,
              borderColor: departmentColor,
            }}
          >
            <Plus size={14} />
            Anexar documento
          </button>
        </div>
      </div>
    </div>
  );
};

const ComprasLayout: React.FC<ComprasLayoutProps> = ({ section }) => {
  const { departmentColor } = useTheme();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;
  const activeSection = section || "visao-geral";

  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [selectedCompra, setSelectedCompra] = useState<Compra | null>(null);

  const categorias = useMemo(() => {
    return Array.from(
      new Set(fixedCompras.map((compra) => compra.categoria))
    ).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, []);

  const centrosCusto = useMemo(() => {
    return Array.from(
      new Set(fixedCompras.map((compra) => compra.centroCusto))
    ).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, []);

  const filteredBySection = useMemo(() => {
    switch (activeSection) {
      case "pendencias":
        return fixedCompras.filter(
          (compra) => getPendenciasDocumentais(compra).length > 0
        );

      case "aguardando-pagamento":
        return fixedCompras.filter(
          (compra) => compra.status === "aguardando_pagamento"
        );

      case "pagas":
        return fixedCompras.filter(
          (compra) => compra.status === "paga" || compra.status === "recebida"
        );

      case "canceladas":
        return fixedCompras.filter((compra) => compra.status === "cancelada");

      case "visao-geral":
      default:
        return fixedCompras;
    }
  }, [activeSection]);

  const filteredItems = useMemo(() => {
    const busca = normalizeTextSearch(filters.busca);

    return filteredBySection.filter((compra) => {
      const buscaMatch =
        !busca ||
        [
          compra.codigo,
          compra.fornecedor,
          compra.cnpjCpf,
          compra.categoria,
          compra.centroCusto,
          compra.solicitante,
          compra.responsavel,
          compra.descricao,
        ].some((field) => normalizeTextSearch(field).includes(busca));

      const statusMatch = !filters.status || compra.status === filters.status;

      const categoriaMatch =
        !filters.categoria || compra.categoria === filters.categoria;

      const centroCustoMatch =
        !filters.centroCusto || compra.centroCusto === filters.centroCusto;

      const pendencias = getPendenciasDocumentais(compra);

      const pendenciaMatch =
        !filters.pendencia ||
        (filters.pendencia === "com_pendencia" && pendencias.length > 0) ||
        (filters.pendencia === "sem_pendencia" && pendencias.length === 0) ||
        (filters.pendencia === "sem_comprovante" &&
          getDocumento(compra, "comprovante_pagamento")?.status ===
            "pendente") ||
        (filters.pendencia === "sem_nota_fiscal" &&
          getDocumento(compra, "nota_fiscal")?.status === "pendente") ||
        (filters.pendencia === "sem_detalhamento" &&
          getDocumento(compra, "detalhamento_compra")?.status === "pendente");

      return (
        buscaMatch &&
        statusMatch &&
        categoriaMatch &&
        centroCustoMatch &&
        pendenciaMatch
      );
    });
  }, [filteredBySection, filters]);

  const resumo = useMemo(() => {
    const total = fixedCompras.length;
    const valorTotal = fixedCompras.reduce(
      (sum, compra) => sum + compra.valorTotal,
      0
    );

    const pendentes = fixedCompras.filter(
      (compra) => getPendenciasDocumentais(compra).length > 0
    ).length;

    const pagas = fixedCompras.filter(
      (compra) => compra.status === "paga" || compra.status === "recebida"
    ).length;

    const aguardandoPagamento = fixedCompras.filter(
      (compra) => compra.status === "aguardando_pagamento"
    ).length;

    return {
      total,
      valorTotal,
      pendentes,
      pagas,
      aguardandoPagamento,
    };
  }, []);

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Compras - ${getSectionTitle(activeSection)}`}
      />

      <div style={styles.content}>
        <section style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>
              <ShoppingCart size={16} />
            </div>
            <div>
              <span style={styles.summaryLabel}>Compras registradas</span>
              <strong style={styles.summaryValue}>{resumo.total}</strong>
            </div>
          </div>

          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>
              <WalletCards size={16} />
            </div>
            <div>
              <span style={styles.summaryLabel}>Valor total</span>
              <strong style={styles.summaryValue}>
                {formatCurrency(resumo.valorTotal)}
              </strong>
            </div>
          </div>

          <div style={styles.summaryCard}>
            <div style={styles.summaryIconWarning}>
              <AlertTriangle size={16} />
            </div>
            <div>
              <span style={styles.summaryLabel}>Com pendência documental</span>
              <strong style={styles.summaryValue}>{resumo.pendentes}</strong>
            </div>
          </div>

          <div style={styles.summaryCard}>
            <div style={styles.summaryIconOk}>
              <PackageCheck size={16} />
            </div>
            <div>
              <span style={styles.summaryLabel}>Pagas / recebidas</span>
              <strong style={styles.summaryValue}>{resumo.pagas}</strong>
            </div>
          </div>
        </section>

        <section style={styles.filterPanel}>
          <div style={styles.filterHeader}>
            <div style={styles.filterTitleArea}>
              <Filter size={15} />
              <strong>Filtros</strong>
            </div>

            <button
              type="button"
              style={styles.clearButton}
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          </div>

          <div style={styles.filterGrid}>
            <div style={styles.searchField}>
              <Search size={14} />
              <input
                style={styles.searchInput}
                value={filters.busca}
                onChange={(event) => updateFilter("busca", event.target.value)}
                placeholder="Buscar por código, fornecedor, categoria, centro de custo..."
              />
            </div>

            <select
              style={styles.filterInput}
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
            >
              <option value="">Todos os status</option>
              <option value="rascunho">Rascunho</option>
              <option value="em_cotacao">Em cotação</option>
              <option value="aguardando_aprovacao">Aguardando aprovação</option>
              <option value="aprovada">Aprovada</option>
              <option value="aguardando_pagamento">Aguardando pagamento</option>
              <option value="paga">Paga</option>
              <option value="recebida">Recebida</option>
              <option value="cancelada">Cancelada</option>
            </select>

            <select
              style={styles.filterInput}
              value={filters.categoria}
              onChange={(event) =>
                updateFilter("categoria", event.target.value)
              }
            >
              <option value="">Todas as categorias</option>
              {categorias.map((categoria) => (
                <option key={categoria} value={categoria}>
                  {categoria}
                </option>
              ))}
            </select>

            <select
              style={styles.filterInput}
              value={filters.centroCusto}
              onChange={(event) =>
                updateFilter("centroCusto", event.target.value)
              }
            >
              <option value="">Todos os centros de custo</option>
              {centrosCusto.map((centroCusto) => (
                <option key={centroCusto} value={centroCusto}>
                  {centroCusto}
                </option>
              ))}
            </select>

            <select
              style={styles.filterInput}
              value={filters.pendencia}
              onChange={(event) =>
                updateFilter("pendencia", event.target.value)
              }
            >
              <option value="">Todas as situações documentais</option>
              <option value="com_pendencia">Com pendência</option>
              <option value="sem_pendencia">Sem pendência</option>
              <option value="sem_comprovante">Sem comprovante</option>
              <option value="sem_nota_fiscal">Sem nota fiscal</option>
              <option value="sem_detalhamento">Sem detalhamento</option>
            </select>
          </div>
        </section>

        <section style={styles.tablePanel}>
          <div style={styles.tableHeader}>
            <div>
              <h2 style={styles.tableTitle}>Compras controladas</h2>
              <span style={styles.tableHint}>
                {filteredItems.length}{" "}
                {plural(filteredItems.length, "registro", "registros")}{" "}
                encontrado(s). Duplo clique na linha abre o detalhamento.
              </span>
            </div>

            <button type="button" style={styles.newPurchaseButton}>
              <Plus size={13} />
              Nova compra
            </button>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Fornecedor</th>
                  <th style={styles.th}>Classificação</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Valor</th>
                  <th style={styles.th}>Documentos</th>
                  <th style={styles.thNoRightBorder}>Conferência</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.map((compra) => (
                  <CompraRow
                    key={compra.id}
                    compra={compra}
                    departmentColor={activeDepartmentColor}
                    onOpen={setSelectedCompra}
                  />
                ))}

                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={7} style={styles.emptyCell}>
                      Nenhuma compra encontrada com os filtros atuais.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <CompraDetailOverlay
        compra={selectedCompra}
        departmentColor={activeDepartmentColor}
        onClose={() => setSelectedCompra(null)}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  summaryCard: {
    minHeight: 72,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    padding: "11px 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  summaryIcon: {
    width: 34,
    height: 34,
    border: "1px solid #dbe4ef",
    background: "#f8fafc",
    color: "#475569",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconWarning: {
    width: 34,
    height: 34,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconOk: {
    width: 34,
    height: 34,
    border: "1px solid #bbf7d0",
    background: "#ecfdf3",
    color: "#166534",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryLabel: {
    display: "block",
    fontSize: 11.2,
    fontWeight: 850,
    color: "#64748b",
    lineHeight: 1.15,
  },

  summaryValue: {
    display: "block",
    marginTop: 3,
    fontSize: 16,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.15,
  },

  filterPanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
  },

  filterHeader: {
    minHeight: 38,
    padding: "7px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  filterTitleArea: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },

  clearButton: {
    height: 28,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    padding: "0 9px",
    fontSize: 11.5,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  filterGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "1.4fr repeat(4, minmax(150px, 1fr))",
    gap: 8,
  },

  searchField: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "0 9px",
    minWidth: 0,
  },

  searchInput: {
    border: 0,
    outline: "none",
    height: "100%",
    flex: 1,
    minWidth: 0,
    fontFamily: "inherit",
    fontSize: 12.3,
    fontWeight: 750,
    color: "#111827",
  },

  filterInput: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 8px",
    fontFamily: "inherit",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    minWidth: 0,
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableHeader: {
    minHeight: 44,
    padding: "8px 10px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  tableTitle: {
    margin: 0,
    fontSize: 13.5,
    fontWeight: 950,
    color: "#111827",
  },

  tableHint: {
    display: "block",
    marginTop: 1,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1080,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  thNoRightBorder: {
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: 0,
    textAlign: "left",
    fontSize: 11,
    fontWeight: 950,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "pointer",
    background: "#ffffff",
    transition: "background 140ms ease, box-shadow 140ms ease",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
  },

  tdNoRightBorder: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: 0,
    overflow: "hidden",
  },

  codeCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  codeIcon: {
    width: 29,
    height: 29,
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  codeInfo: {
    minWidth: 0,
  },

  codeText: {
    fontSize: 12.5,
    fontWeight: 950,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mainText: {
    fontSize: 12.4,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  mutedText: {
    marginTop: 1,
    fontSize: 10.8,
    fontWeight: 650,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  valueText: {
    fontSize: 12.8,
    fontWeight: 950,
    color: "#111827",
    whiteSpace: "nowrap",
  },

  badge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  documentSummary: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  newPurchaseButton: {
    height: 26,
    padding: "0 9px",
    border: "1px solid #d7dde5",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    fontSize: 11.4,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },

  documentOk: {
    fontSize: 11.5,
    fontWeight: 850,
    color: "#166534",
  },

  documentPending: {
    fontSize: 11.5,
    fontWeight: 850,
    color: "#9a3412",
  },

  documentComplete: {
    fontSize: 11.5,
    fontWeight: 850,
    color: "#166534",
  },

  emptyCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
  },

  primaryButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  overlayPanel: {
    width: "min(980px, 100%)",
    maxHeight: "calc(100vh - 36px)",
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  overlayEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },

  overlayTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    fontSize: 12.4,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.35,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #d8dde5",
    background: "#ffffff",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  detailContent: {
    padding: 18,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  detailTopGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  detailCard: {
    minHeight: 74,
    border: "1px solid #dfe3e8",
    background: "#f8fafc",
    padding: "10px 11px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
  },

  detailLabel: {
    fontSize: 10.8,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.25,
  },

  detailValue: {
    marginTop: 3,
    fontSize: 13.2,
    fontWeight: 950,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailMeta: {
    marginTop: 2,
    fontSize: 11.2,
    fontWeight: 700,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailBadgeArea: {
    marginTop: 7,
  },

  detailSection: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
  },

  detailSectionHeader: {
    minHeight: 38,
    padding: "8px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#334155",
    fontSize: 12.5,
    fontWeight: 900,
  },

  documentsGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
  },

  documentCard: {
    border: "1px solid #dfe3e8",
    background: "#ffffff",
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 0,
  },

  documentCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },

  documentCardTitleArea: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    color: "#334155",
  },

  documentCardTitle: {
    fontSize: 12.3,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  documentInfoBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
  },

  documentInfoLabel: {
    fontSize: 10.5,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  documentInfoValue: {
    fontSize: 11.6,
    fontWeight: 750,
    color: "#334155",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  documentActions: {
    paddingTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  documentActionButton: {
    height: 28,
    padding: "0 9px",
    border: "1px solid",
    background: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    fontSize: 11.4,
    fontWeight: 850,
    fontFamily: "inherit",
  },

  infoGrid: {
    padding: 10,
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
  },

  infoItem: {
    minHeight: 54,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    padding: "8px 9px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
  },

  observationBox: {
    margin: "0 10px 10px",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    color: "#334155",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  overlayFooter: {
    minHeight: 48,
    padding: "10px 18px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },

  cancelButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
};

export default ComprasLayout;
